import { useCallback, useEffect, useRef, useState } from 'react';
import {
  findVideoCapableModel,
  getModelCapability,
  isVideoCapableModel,
} from '@inference.engine/model-registry/web';
import { track } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';

const videoOnlyOnGoogle = (): string =>
  i18n.t('chats:chats.modelCompatibilityGuard.videoOnlySupportedByGoogleModels');

/** How long a transient alert stays on screen before auto-dismissing (ms). */
const ALERT_AUTO_DISMISS_MS = 30_000;

const buildSwitchedMessage = (modelLabel: string): string =>
  i18n.t('chats:chats.modelCompatibilityGuard.modelSwitched', { modelLabel });

/**
 * True for alerts that explain a *blocking* state rather than report a completed
 * action. When the agent has no video-capable model, send is disabled and every
 * model in the picker is greyed out — this message is the only thing telling the
 * user why, and the guard effect's deps are unchanged afterwards so it would
 * never be re-raised. Such alerts must not auto-dismiss.
 */
const isBlockingAlert = (message: string): boolean => message === videoOnlyOnGoogle();

interface UseModelCompatibilityGuardParams {
  /** Current chat agent id — for analytics. */
  agentId: string;
  /** Chat/session id when one exists (absent for a brand-new, unsent chat). */
  sessionId?: string;
  /** Whether the composer currently has at least one video attachment. */
  hasVideoAttachment: boolean;
  /** The model currently selected in the composer. */
  selectedModel: string | undefined;
  /** Models the current agent has enabled (the switch target must be one of these). */
  enabledModels: readonly string[] | undefined;
  /** Applies a model change (same handler the model picker uses). */
  onModelChange: (model: string) => void;
}

interface UseModelCompatibilityGuardResult {
  /** The message to render in a `ChatAlert`, or null when nothing to show. */
  alert: string | null;
  /** Dismisses the current alert. */
  dismissAlert: () => void;
}

/**
 * Keeps the selected model compatible with the composer's attachments.
 *
 * Today the only rule is video → Google: if a video is attached while a
 * non-Google model is selected, the model is auto-switched to the agent's first
 * video-capable enabled model and an informational alert is surfaced. When the
 * agent has no video-capable model to switch to, the alert is shown without a
 * switch.
 *
 * The alert is informational (not an error): it stays until the user dismisses
 * it via the X, or auto-dismisses after {@link ALERT_AUTO_DISMISS_MS}. Removing
 * the video does not hide it. Re-selecting an incompatible model while a video
 * is still attached re-triggers the switch.
 *
 * The no-fallback message is the exception: it explains why send is disabled, so
 * it never auto-dismisses and is only cleared by the user.
 */
export const useModelCompatibilityGuard = ({
  agentId,
  sessionId,
  hasVideoAttachment,
  selectedModel,
  enabledModels,
  onModelChange,
}: UseModelCompatibilityGuardParams): UseModelCompatibilityGuardResult => {
  const [alert, setAlert] = useState<string | null>(null);
  // The incompatible model we last acted on, to dedupe StrictMode double-fires
  // and re-renders that happen before the store's model update propagates.
  const handledModelRef = useRef<string | null>(null);

  const dismissAlert = useCallback(() => setAlert(null), []);

  useEffect(() => {
    // No video attached: reset the dedupe so a later re-attach can trigger again.
    // The alert itself is NOT auto-cleared here — it persists until the user
    // dismisses it via the ChatAlert's X button, so a switch that already
    // happened stays acknowledged even after the video is removed.
    if (!hasVideoAttachment) {
      handledModelRef.current = null;
      return;
    }

    // Current model already accepts video (including right after our switch):
    // nothing to do, and reset the dedupe so re-selecting an incompatible model
    // triggers again. Keep any informational alert already on screen.
    if (!selectedModel || isVideoCapableModel(selectedModel)) {
      handledModelRef.current = null;
      return;
    }

    // Incompatible selection — act once per distinct incompatible model.
    if (handledModelRef.current === selectedModel) return;

    const fallback = findVideoCapableModel(enabledModels ?? []);
    if (!fallback) {
      // No video-capable model enabled for this agent (or `enabledModels` hasn't
      // loaded yet): inform, but do NOT mark this model handled. If a
      // video-capable model appears once the agent's models load, the effect
      // re-runs and the switch below still fires. Re-setting the same message is
      // a no-op, so this does not thrash while the list is empty.
      setAlert(videoOnlyOnGoogle());
      return;
    }

    // Commit the switch and dedupe further runs for this incompatible model.
    handledModelRef.current = selectedModel;
    const fromProvider = getModelCapability(selectedModel)?.provider;
    const target = getModelCapability(fallback);
    onModelChange(fallback);
    setAlert(buildSwitchedMessage(target?.label ?? fallback));

    if (fromProvider && target?.provider) {
      track('chat_model_auto_switched', {
        agent_id: agentId,
        session_id: sessionId,
        from_provider: fromProvider,
        to_provider: target.provider,
        reason: 'video_attachment',
      });
    }
  }, [hasVideoAttachment, selectedModel, enabledModels, agentId, sessionId, onModelChange]);

  // Auto-dismiss transient alerts after a fixed delay. The timer resets whenever
  // the alert message changes (a new switch), and is cleared on manual dismiss or
  // unmount. Blocking alerts are exempt — see {@link isBlockingAlert}.
  useEffect(() => {
    if (!alert || isBlockingAlert(alert)) return;
    const timeoutId = setTimeout(() => setAlert(null), ALERT_AUTO_DISMISS_MS);
    return () => clearTimeout(timeoutId);
  }, [alert]);

  return { alert, dismissAlert };
};
